import React, { useState } from 'react';
import { ArrowLeft, Send, Bell, CheckCircle2, ThumbsUp, ThumbsDown, Edit } from 'lucide-react';
import inAppMessageService from '../../services/inAppMessage.service';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getThemeColors } from '../../config/theme';
import { TemplateType } from '../../types/inAppMessageTypes';
import { resolvePrimaryAction } from '../../constants/inAppMessageDestinations';

export const PayloadPreview = ({ config, selectedEmployeeIds, employees, onBack, onSent, onEdit }) => {
  const { isDarkMode, t } = usePreferences();
  const colors = getThemeColors(isDarkMode);
  const [sending, setSending] = useState(false);
  const [approved, setApproved] = useState(false);

  const generatePayload = () => {
    const shouldHaveAction = config.type === TemplateType.TOP_BANNER || config.content.showPrimaryButton;
    const resolvedAction = shouldHaveAction
      ? resolvePrimaryAction(config.content.primaryAction)
      : null;

    return {
      template: config.type,
      content: {
        title: config.content.title || undefined,
        body: config.content.body,
        imageUrl: config.content.showImage ? config.content.imageUrl : undefined,
        action: resolvedAction
          ? {
              label:
                config.content.buttonText ||
                (config.type === TemplateType.TOP_BANNER ? 'View' : 'Action'),
              type: resolvedAction.type,
              value: resolvedAction.value,
              params: resolvedAction.params,
            }
          : null,
        secondaryAction: (config.content.showSecondaryButton && config.type !== TemplateType.TOP_BANNER) ? {
          label: config.content.secondaryButtonText,
        } : null
      },
      styles: config.styles
    };
  };

  const payload = generatePayload();

  const selectedEmployees = employees.filter(emp => selectedEmployeeIds.includes(emp.id));
  const selectedWithToken = selectedEmployees.filter(emp => emp.has_fcm_token === 1).length;
  const selectedWithoutToken = selectedEmployees.filter(emp => emp.has_fcm_token !== 1).length;

  const handleSend = async () => {
    if (!approved) return;
    
    setSending(true);
    try {
      const response = await inAppMessageService.sendInAppMessage(payload, selectedEmployeeIds);
      onSent(response.data);
    } catch (error) {
      console.error('Error sending in-app messages:', error);
      alert(error.response?.data?.message || 'Error sending messages. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const renderInAppContent = () => {
    const { type, content, styles } = config;
    const isFull = type === TemplateType.FULL_SCREEN;
    const isCenter = type === TemplateType.CENTER_MODAL;
    const isBanner = type === TemplateType.TOP_BANNER;
    const showSecondary = (isFull || isCenter) && content.showSecondaryButton;
    const showImage = content.showImage && content.imageUrl;
    const hasImage = isBanner && showImage;

    // Special rendering for TOP_BANNER
    if (isBanner) {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.preview.inAppMessagePreview')}</h3>
            <div 
              className="rounded-xl overflow-hidden shadow-lg cursor-pointer active:opacity-90 transition-opacity"
              style={{ 
                backgroundColor: styles.backgroundColor,
                borderRadius: `${styles.borderRadius}px`,
                maxWidth: '400px',
                margin: '0 auto'
              }}
            >
              <div className={`flex flex-row items-center p-4 gap-4 ${hasImage ? '' : 'justify-center'}`}>
                {hasImage && (
                  <img 
                    src={content.imageUrl} 
                    alt="Banner" 
                    className="object-cover flex-shrink-0"
                    style={{
                      width: `${styles.imageSize || 48}px`,
                      height: `${styles.imageSize || 48}px`,
                      borderRadius: `${styles.imageBorderRadius || 24}px`
                    }}
                  />
                )}
                <div className={hasImage ? "flex-1 min-w-0" : "flex-1 min-w-0 text-center"}>
                  {content.title && (
                    <h3 
                      className={`font-bold ${hasImage ? 'truncate' : ''} mb-1`}
                      style={{ 
                        color: styles.textColor,
                        fontSize: `${Math.max(14, styles.titleFontSize * 0.7)}px`
                      }}
                    >
                      {content.title}
                    </h3>
                  )}
                  <p 
                    className={`opacity-90 leading-relaxed ${hasImage ? 'line-clamp-2' : ''}`}
                    style={{ 
                      color: styles.textColor,
                      fontSize: `${Math.max(12, styles.bodyFontSize * 0.75)}px`
                    }}
                  >
                    {content.body}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Regular rendering for other templates
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-3" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.preview.inAppMessagePreview')}</h3>
          <div 
            className="rounded-xl overflow-hidden shadow-lg"
            style={{ 
              backgroundColor: styles.backgroundColor,
              borderRadius: `${styles.borderRadius}px`,
              maxWidth: '400px',
              margin: '0 auto'
            }}
          >
            {showImage && (
              <img 
                src={content.imageUrl} 
                alt="Hero" 
                className="w-full object-cover"
                style={{ height: '200px' }}
              />
            )}
            
            <div className={`p-6 ${isFull ? 'py-8' : ''}`}>
              {content.title && (
                <h3 
                  className="font-bold mb-3 leading-tight"
                  style={{ 
                    color: styles.textColor,
                    fontSize: `${styles.titleFontSize}px`
                  }}
                >
                  {content.title}
                </h3>
              )}
              <p 
                className="mb-6 opacity-90 leading-relaxed whitespace-pre-line"
                style={{ 
                  color: styles.textColor,
                  fontSize: `${styles.bodyFontSize}px`
                }}
              >
                {content.body}
              </p>
              
              {content.showPrimaryButton && (
                <button
                  className="py-3 px-6 font-semibold w-full active:scale-95 transition-transform shadow-sm mb-3"
                  style={{ 
                    backgroundColor: styles.buttonColor, 
                    color: styles.buttonTextColor,
                    borderRadius: '8px'
                  }}
                >
                  {content.buttonText}
                </button>
              )}

              {showSecondary && (
                <button 
                  className="py-3 px-6 text-sm font-medium w-full hover:opacity-80 active:scale-95 transition-all" 
                  style={{ 
                    backgroundColor: styles.secondaryButtonColor,
                    color: styles.secondaryButtonTextColor,
                    borderRadius: '8px'
                  }}
                >
                  {content.secondaryButtonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="p-6 border-b" style={{ borderColor: colors.border }}>
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg transition-colors"
            style={{ color: colors.textSecondary }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.backgroundSecondary}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.preview.title')}</h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.backgroundSecondary }}>
            <div className="text-xs mb-1" style={{ color: colors.textSecondary }}>{t('pages.inAppMessages.preview.selected')}</div>
            <div className="text-lg font-semibold" style={{ color: colors.textPrimary }}>{selectedEmployeeIds.length}</div>
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)' }}>
            <div className="text-xs mb-1" style={{ color: colors.success }}>{t('pages.inAppMessages.preview.withFcmToken')}</div>
            <div className="text-lg font-semibold" style={{ color: colors.success }}>{selectedWithToken}</div>
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)' }}>
            <div className="text-xs mb-1" style={{ color: colors.warning }}>{t('pages.inAppMessages.preview.withoutToken')}</div>
            <div className="text-lg font-semibold" style={{ color: colors.warning }}>{selectedWithoutToken}</div>
          </div>
        </div>

        {selectedWithoutToken > 0 && (
          <div className="mt-4 p-3 border rounded-lg" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)', borderColor: colors.warning }}>
            <p className="text-sm" style={{ color: colors.warning }}>
              ⚠️ {selectedWithoutToken} {t('pages.inAppMessages.preview.warningNoToken')}
            </p>
          </div>
        )}
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Push Notification Preview */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: colors.textPrimary }}>
              <Bell size={18} />
              {t('pages.inAppMessages.preview.pushNotification')}
            </h3>
            <div 
              className="p-4 rounded-lg shadow-md max-w-sm mx-auto"
              style={{ 
                backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                border: `1px solid ${colors.border}`
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex-shrink-0" style={{ backgroundColor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Bell size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs mb-1" style={{ color: colors.textTertiary }}>Effzone</div>
                  <div className="font-semibold mb-1" style={{ color: colors.textPrimary }}>
                    {config.content.title || 'New Message'}
                  </div>
                  <div className="text-sm line-clamp-2" style={{ color: colors.textSecondary }}>
                    {config.content.body || 'You have a new message'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* In-App Message Preview */}
          {renderInAppContent()}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
        {/* Approval CTA */}
        <div className="mb-4 flex items-center justify-center gap-3">
          <button
            onClick={() => {
              if (onEdit) onEdit();
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
            style={{
              backgroundColor: colors.background,
              color: colors.textPrimary,
              border: `2px solid ${colors.border}`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.warning || '#f59e0b';
              e.currentTarget.style.color = colors.warning || '#f59e0b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.color = colors.textPrimary;
            }}
          >
            <ThumbsDown size={18} />
            <span>{t('pages.inAppMessages.preview.iDontLikeIt')}</span>
          </button>
          <button
            onClick={() => setApproved(!approved)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              approved ? 'shadow-md' : ''
            }`}
            style={{
              backgroundColor: approved ? colors.primary : colors.background,
              color: approved ? '#ffffff' : colors.textPrimary,
              border: `2px solid ${approved ? colors.primary : colors.border}`
            }}
            onMouseEnter={(e) => {
              if (!approved) {
                e.currentTarget.style.borderColor = colors.primary;
              }
            }}
            onMouseLeave={(e) => {
              if (!approved) {
                e.currentTarget.style.borderColor = colors.border;
              }
            }}
          >
            <ThumbsUp size={18} />
            <span>{approved ? t('pages.inAppMessages.preview.approved') : t('pages.inAppMessages.preview.iLikeIt')}</span>
          </button>
        </div>

        <button
          onClick={handleSend}
          disabled={sending || selectedEmployeeIds.length === 0 || !approved}
          className="w-full text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          style={{ backgroundColor: colors.primary }}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = colors.primaryHover;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.primary;
          }}
        >
          {sending ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{t('pages.inAppMessages.preview.sending')}</span>
            </>
          ) : (
            <>
              <Send size={18} />
              <span>{t('pages.inAppMessages.preview.sendMessages')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
